import { useEffect, useState } from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { TransactionType } from "@hl/shared-features/lib/apollo/graphql.generated";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import {
  TransactionStateType,
  useTransactionsDispatch,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import { logError } from "@hl/shared-features/lib/services/logger";
import {
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Space,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { ethers } from "ethers";
import { isEmpty } from "lodash";

import { _CollectionType, V1Royalty } from "apollo/graphql.generated";
import { ALCHEMY_API_KEY_ENS } from "config";

/* eslint-disable  @typescript-eslint/no-explicit-any */
type Props = {
  collectionId: string;
  editionId?: string | null;
  address: string;
  royalty: V1Royalty;
  chainId: number;
  collectionType: _CollectionType;
  isOpened: boolean;
  onClose: () => void;
  refetchCollection?: any;
  refetchAuction?: any;
};

interface EditRoyaltyFormValues {
  royaltyRecipientAddress: string;
  royaltyEOA: string;
  royaltyAmount: number;
}

export const EditRoyalties = ({
  isOpened,
  onClose,
  royalty,
  chainId,
  collectionId,
  editionId,
  collectionType,
  address,
  refetchCollection,
  refetchAuction,
}: Props) => {
  const { walletAddress } = useAuth();
  const getNewTxnId = () => `txn-${Date.now()}`;
  const [error, setError] = useState<string>("");
  const [txnId, setTxnId] = useState<string>(getNewTxnId());

  const transactionDispatch = useTransactionsDispatch();
  const transactionState = useTransactionState(txnId);

  const refetch = () => {
    refetchCollection();
    refetchAuction();
  };

  const form = useForm<EditRoyaltyFormValues>({
    initialValues: {
      royaltyAmount: parseFloat(royalty.royaltyPercentageString || "0"),
      royaltyRecipientAddress: royalty.royaltyRecipient || "",
      royaltyEOA: royalty.royaltyRecipient || "",
    },
    validate: (values) => ({
      royaltyRecipientAddress: isEmpty(values.royaltyRecipientAddress)
        ? "Royalty Recipient Address is required"
        : null,
      royaltyAmount:
        values.royaltyAmount === undefined || values.royaltyAmount === null
          ? "Royalty Amount is required"
          : values.royaltyAmount < 0 || values.royaltyAmount > 1000
          ? "Invalid Royalty Amount"
          : null,
    }),
  });

  const closeModal = () => {
    refetch();
    form.reset();
    onClose();
  };

  const onSubmit = form.onSubmit(async (values) => {
    setError("");

    if (!collectionId) {
      throw new Error("Collection without id");
    }

    try {
      if (walletAddress) {
        const txnId = getNewTxnId();

        const royalties = {
          recipientAddress: values.royaltyRecipientAddress,
          royaltyPercentageBPS: BigInt(values.royaltyAmount * 100),
        };
        let method, params;
        switch (collectionType) {
          case _CollectionType.OpenEdition:
          case _CollectionType.Series:
          case _CollectionType.GenerativeSeries:
            method = "setDefaultRoyalty";
            params = [royalties];
            break;
          case _CollectionType.OneOfOne:
          case _CollectionType.LimitedEdition:
            if (editionId === null || editionId === undefined) {
              throw new Error("EditionId is required");
            }
            method = "setGranularRoyalties";
            params = [[BigInt(editionId)], [royalties]];
            break;
        }

        if (!method || !params) throw new Error("Invalid collection");
        const contractData = {
          address: address,
          args: JSON.stringify(params),
          chainId,
          method,
          value: "0",
          bytecode: null,
        };

        transactionDispatch?.({
          type: "START_TRANSACTION",
          payload: {
            id: txnId,
            entityId: collectionId,
            transactionType: TransactionType.EVM_UPDATE_ROYALTY,
            collectionType: collectionType,
            args: contractData,
            metadata: {
              collectionId,
              nftContractAddress: walletAddress,
              txType: TransactionType.EVM_UPDATE_ROYALTY,
            },
          },
        });
        setTxnId(txnId);
      }
    } catch (e) {
      setError("Failed to save changes.");
      logError({
        e,
        message: "[EditRoyaltiesModal] Failed to update",
      });
    }
  });

  useEffect(() => {
    form.validate();
  }, [form.values]);

  const ensUrl = `https://eth-mainnet.alchemyapi.io/v2/${ALCHEMY_API_KEY_ENS}`;
  const provider = ALCHEMY_API_KEY_ENS
    ? new ethers.providers.JsonRpcProvider(ensUrl)
    : ethers.getDefaultProvider();

  const onSetRoyaltyEOA = async (val: string) => {
    form.setFieldValue("royaltyRecipientAddress", val);
    if (val.endsWith(".eth")) {
      const address = await provider.resolveName(val);
      if (!address) {
        form.setFieldError("royaltyEOA", "Invalid ENS");
      } else {
        form.setFieldValue("royaltyRecipientAddress", address);
      }
    }
  };

  let buttonLabel = "Save Changes";
  let buttonDisabled = false;
  if (transactionState && !transactionState.error && !!transactionState.args) {
    if (transactionState.type === TransactionStateType.WaitingSignedTx) {
      buttonLabel = "Waiting for user signature...";
      buttonDisabled = true;
    } else if (transactionState.type !== TransactionStateType.Done) {
      buttonLabel = "Updating ...";
      buttonDisabled = true;
    }
  }

  useEffect(() => {
    if (transactionState?.type === TransactionStateType.Done) {
      closeModal();
    }
  }, [transactionState?.type]);

  return (
    <Modal size="lg" opened={isOpened} onClose={onClose} title="Edit Royalty">
      <Box mx="auto">
        <form onSubmit={onSubmit}>
          <NumberInput
            label="Royalty amount"
            min={0}
            max={100}
            precision={2}
            placeholder={0}
            removeTrailingZeros={true}
            rightSection={
              <Text mr="xs" color={TEXT_COLOR.SECONDARY}>
                %
              </Text>
            }
            {...form.getInputProps("royaltyAmount")}
          />
          <Space h="lg" />
          <TextInput
            placeholder="e.g. vitalik.eth, 0x0000000000000000000000000000000000000000"
            spellCheck={false}
            onInput={(e) => onSetRoyaltyEOA(e.currentTarget.value)}
            label={
              <>
                <Group spacing={4}>Royalty payout address</Group>
              </>
            }
            {...form.getInputProps("royaltyEOA")}
          />
          <ModalSection>
            <Button type="submit" size="xl" fullWidth disabled={buttonDisabled}>
              {buttonLabel}
            </Button>
            {error && (
              <Text size="xs" mt={5} color={STATUS_COLOR.ERROR}>
                {error}
              </Text>
            )}
            <Text size="xs" align="center" mt={8}>
              This action requires a transaction
            </Text>
          </ModalSection>
        </form>
      </Box>
    </Modal>
  );
};
